import {
  FileImageOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { message, Modal, Upload } from 'antd';
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
  UploadProps,
} from 'antd/lib/upload/interface';
import BgModalConsent from 'assets/images/driver-portal/bg-modal-consent.png';
import { Button, Text as TextComponent, Title } from 'components';
import { theme } from 'configs/styled';
import React, { useEffect, useState } from 'react';
import { uploadService } from 'services/driverPortal';
import { INFINITY_DATE } from 'utils/dateUtils';

export interface IRequestUpload {
  uploadFileForm?: FormData;
}

const courierName = 'allnow';

export interface IUploadImageProps {
  onChange: (value: IRequestUpload) => void;
  onRemove?: (file: UploadFile) => void;
  folder: string;
  pathImage?: string;
  nameImage?: string;
  disabled?: boolean;
  onChecked?: boolean | undefined;
  consent?: { title?: string; content?: string };
  isPdf?: boolean;
  fileName?: string;
}

const uploadFailNotification = (_message: string) => {
  return message.error(_message, 5);
};

const buildPayloadUploadImage = (files: File, folderType: string) => {
  const formData = new FormData();

  formData.append('files', files);
  formData.append('folderType', folderType);
  formData.append('expireDate', INFINITY_DATE().toString());

  return formData;
};

const UploadImage: React.FC<IUploadImageProps> = props => {
  const {
    onChange,
    onRemove,
    pathImage,
    nameImage,
    disabled,
    folder,
    onChecked = undefined,
    consent,
    isPdf = false,
    fileName = '',
  } = props;

  const [fileLists, setFileLists] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [consentModal, setConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState<boolean | undefined>(
    undefined,
  );
  const [previewImage, setPreviewImage] = useState<string | undefined>('');
  const [previewTitle, setPreviewTitle] = useState<string | undefined>('');
  const [previewPdf, setPreviewPdf] = useState<string>('');

  useEffect(() => {
    const getImage = async (path: string) => {
      let url = path;

      if (!path.match(/^blob:/)) {
        url = await uploadService.getFile(path);
      }

      const fileList = [
        {
          uid: '1',
          name: fileName || nameImage || 'document',
          fileName: fileName || path,
          thumbUrl: isPdf ? undefined : url,
          url,
          status: 'success',
        },
      ] as UploadFile[];

      setFileLists(fileList);
    };

    if (pathImage) {
      getImage(pathImage);
    } else {
      setFileLists([]);
    }
  }, [pathImage, nameImage, isPdf, fileName]);

  const handlePreview = async (file: UploadFile) => {
    if (!pathImage) return;

    const fileUrl = (file.url || file.thumbUrl) as string | undefined;
    const title = file.name || fileName || nameImage || 'document';
    if (!fileUrl) return;

    setPreviewTitle(title);
    setPreviewVisible(true);

    if (isPdf) {
      setPreviewPdf(fileUrl);
      setPreviewImage('');
      return;
    }

    setPreviewPdf('');
    setPreviewImage(fileUrl);
  };

  const handleRequestUpload = async (
    options: Parameters<NonNullable<UploadProps['customRequest']>>[0],
  ) => {
    const { file, onSuccess } = options;

    const folderType = `${courierName}-acquisition-${folder}`;
    const uploadFileForm = buildPayloadUploadImage(file as File, folderType);

    onChange({ uploadFileForm });
    onSuccess?.(uploadFileForm);
  };

  const handleChange = (uploadChange: UploadChangeParam) => {
    const { fileList } = uploadChange;

    const files = fileList.map(file => {
      if (!file.status || file.status === 'error') {
        const { status = 'error', uid, name, size } = file;
        return {
          uid,
          name,
          status,
          size,
        };
      }

      return {
        ...file,
        name: file.name || fileName || nameImage || 'document',
      };
    });

    setFileLists(files as UploadFile[]);
  };

  const isValidateAcceptFileTypes = (type: string, name?: string) => {
    const acceptFileTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/gif',
      'image/webp',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence',
      // 'application/pdf',
      // 'application/x-pdf',
      // 'application/acrobat',
      // 'applications/vnd.pdf',
      // 'text/pdf',
      // 'text/x-pdf',
    ];

    const lowerName = (name || '').toLowerCase();
    const isPdfByName = lowerName.endsWith('.pdf');

    return acceptFileTypes.includes(type) || isPdfByName;
  };

  const isValidateLimitSize = (size: number) => {
    return size / 1024 / 1024 < 10;
  };

  const validateFile = (file: RcFile) => {
    const { type, size, name } = file;

    if (!isValidateAcceptFileTypes(type, name)) {
      uploadFailNotification(
        'ขออภัย ไม่สามารถอัพโหลดได้ ไฟล์ต้องเป็น .jpg .jpeg .png .webp .heic .heif .gif เท่านั้น',
      );
      return false;
    }

    if (!isValidateLimitSize(size)) {
      uploadFailNotification(
        'ขออภัย ไม่สามารถอัพโหลดได้ ไฟล์ของคุณต้องมีขนาดน้อยกว่า 10 Mb',
      );
      return false;
    }

    return true;
  };

  const handleRemove = async (file: UploadFile) => {
    const { uid, status } = file;
    const files = fileLists.filter(item => item.uid !== uid);

    onChange({});
    setFileLists(files);

    if (status !== 'error') {
      onRemove && onRemove(file);
    }
  };

  const handleCancel = () => setPreviewVisible(false);

  const shouldDisabledUploadButton = () => {
    if (disabled || fileLists.length === 1) return true;
    return Boolean(pathImage);
  };

  useEffect(() => {
    if (onChecked !== undefined) {
      setConsentChecked(onChecked);

      if (!onChecked && fileLists[0]?.fileName) {
        handleRemove(fileLists[0]);
      }
    }
  }, [onChecked, fileLists]);

  const handleClickModal = () => {
    if (consentChecked !== undefined) {
      setConsentModal(!consentChecked);
    }
  };

  const handleCloseModal = () => {
    setConsentModal(false);
  };

  return (
    <>
      <div className={isPdf ? 'upload-file-pdf' : 'upload-file-image'}>
        <style>
          {`
    .upload-file-image .ant-upload-list-item-name {
      color: #666666 !important;
    }

    .upload-file-image .ant-upload-list-item-name:hover {
      color: #666666 !important;
    }

    .upload-file-image .ant-upload-list-item-info > span {
      color: #666666 !important;
    }

    .upload-file-pdf .ant-upload-list-item-name {
      color: #666666 !important;
      cursor: default !important;
    }

    .upload-file-pdf .ant-upload-list-item-name:hover {
      color: #666666 !important;
      cursor: default !important;
    }

    .upload-file-pdf .ant-upload-list-item-info > span {
      color: #666666 !important;
      cursor: default !important;
    }

    .upload-file-pdf .ant-upload-list-item {
      position: relative;
      padding-right: 28px !important;
    }

    .upload-file-pdf .ant-upload-list-item-card-actions,
    .upload-file-pdf .ant-upload-list-item-actions {
      opacity: 1 !important;
      visibility: visible !important;
    }

    .upload-file-pdf .ant-upload-list-item-card-actions-btn,
    .upload-file-pdf .ant-upload-list-item-action {
      opacity: 1 !important;
      visibility: visible !important;
      display: inline-block !important;
    }

    .upload-file-pdf .ant-upload-list-item .anticon-delete,
    .upload-file-pdf .ant-upload-list-item .anticon-close,
    .upload-file-pdf .ant-upload-list-item .ant-upload-list-item-card-actions-btn {
      opacity: 1 !important;
      visibility: visible !important;
    }
  `}
        </style>

        <Upload
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif"
          listType={isPdf ? 'text' : 'picture'}
          beforeUpload={validateFile}
          onChange={handleChange}
          onRemove={handleRemove}
          customRequest={handleRequestUpload}
          fileList={fileLists}
          onPreview={handlePreview}
          iconRender={() =>
            isPdf ? (
              <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            ) : (
              <FileImageOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
            )
          }
          showUploadList={{
            showRemoveIcon: !disabled,
            showPreviewIcon: !isPdf,
          }}
          disabled={!consentChecked && consentChecked !== undefined}
        >
          <Button
            disabled={shouldDisabledUploadButton()}
            icon={<UploadOutlined />}
            onClick={handleClickModal}
          >
            อัพโหลดรูป
          </Button>
        </Upload>
      </div>

      {isPdf ? (
        <div></div>
      ) : (
        <Modal
          visible={previewVisible}
          title="Preview"
          footer={null}
          onCancel={handleCancel}
        >
          <img
            alt={previewTitle}
            style={{ width: '100%' }}
            src={previewImage}
          />
        </Modal>
      )}

      {consent ? (
        <Modal
          visible={consentModal}
          footer={null}
          closeIcon={true}
          maskClosable={false}
          closable={false}
        >
          <div
            style={{
              width: '60%',
              position: 'relative',
              display: 'block',
              margin: '0 auto',
            }}
          >
            <img src={BgModalConsent} style={{ width: '100%' }} />
          </div>
          <div
            style={{
              marginTop: theme.spacing.xxl,
              marginBottom: theme.spacing.lg,
            }}
          >
            <Title
              style={{
                fontFamily: 'Noto Sans, Noto Sans Thai',
                fontSize: theme.fontSize.lg,
              }}
            >
              {consent?.title}
            </Title>
            <TextComponent
              style={{
                fontFamily: 'Noto Sans, Noto Sans Thai',
                fontSize: theme.fontSize.sm,
              }}
            >
              {consent?.content}
            </TextComponent>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Button
              style={{
                fontFamily: 'Noto Sans, Noto Sans Thai',
                color: 'white',
                background: '#1890FF',
                border: '1px solid #1890FF',
              }}
              onClick={handleCloseModal}
            >
              ปิดหน้าต่างนี้
            </Button>
          </div>
        </Modal>
      ) : (
        ''
      )}
    </>
  );
};

UploadImage.defaultProps = {
  disabled: true,
};

export default UploadImage;
